import React, { useState, useEffect } from 'react';
import { Container, TextField, Button, Typography, Grid, Paper, Avatar, styled, CircularProgress, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { createUnverifiedUserInAzure, validateSignupJwtToken } from '../services/apiservice';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Helmet } from 'react-helmet-async';
import Loader from '../components/ProgressBars/BladeLoader';
import InvalidTokenScreen from './InvalidTokenScreen';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import { makeStyles } from '@mui/styles';

const RedAsterisk = styled('span')({
  color: 'red',
});

const styles = {
  textCenter: {
    textAlign: 'center',
    color: '#666',
  },
  smallText: {
    fontSize: '0.8rem',
  },
};

const useStyles = makeStyles((theme) => ({
  modalContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(4),
    borderRadius: '8px',
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[5],
  },
  icon: {
    fontSize: '48px',
    color: theme.palette.success.main,
    marginBottom: theme.spacing(2),
  },
  button: {
    marginTop: theme.spacing(2),
  },
}));

const validationSchema = Yup.object({
  //   email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .matches(
      /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{8,}$/,
      'Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character.'
    )
    .required('Password is required'),
  firstName: Yup.string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters long')
    .max(50, 'First name cannot exceed 50 characters'),
  lastName: Yup.string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters long')
    .max(50, 'Last name cannot exceed 50 characters'),
});

const UserSignUp = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isValidToken, setIsValidToken] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [email, setEmail] = useState('');
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const redirectTo = params.get('redirectTo') || '/';
  const token = params.get('token');

  useEffect(() => {
    const validateToken = async () => {
      try {
        const response = await validateSignupJwtToken(token);
        if (response.success) {
          setEmail(response.UserEmail);
          formik.setFieldValue('email', response.UserEmail);
          setIsValidToken(true);
        } else if (response.message === 'User account already created.') {
          navigate(redirectTo);
        } else {
          setErrorMessage('Invalid or expired token');
          setIsValidToken(false);
        }
      } catch (error) {
        console.error('Error validating token:', error);
        setErrorMessage('Failed to validate token');
        setIsValidToken(false);
      } finally {
        setTokenLoading(false);
      }
    };

    if (token) {
      validateToken();
    } else {
      setIsValidToken(false);
      setErrorMessage('No token provided');
      setTokenLoading(false);
    }
  }, [token]);

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: email,
      password: '',
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setErrorMessage('');
      setSuccessMessage('');
      setLoading(true);

      const filteredValues = Object.fromEntries(
        Object.entries(values).filter(([key, value]) => value !== '' && value !== null)
      );

      try {
        const response = await createUnverifiedUserInAzure(
          filteredValues.firstName,
          filteredValues.lastName,
          filteredValues.email,
          filteredValues.password
        );
        if (response.success && response.message === 'User created successfully') {
          navigate(redirectTo);
          setSuccessMessage('User Created Successfully');
        } else {
          setErrorMessage('User already exists');
        }
      } catch (error) {
        console.error('Error creating User:', error);
        if (error.response && error.response.data && error.response.data.message === 'User already exists') {
          setErrorMessage('An User account with this email already exists, please try a different one.');
        } else {
          setErrorMessage('Error creating User');
        }
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    },
  });

  if (tokenLoading) {
    return (
      <div>
        <Loader />
      </div>
    );
  }

  if (!isValidToken) {
    const errorHeader = errorMessage === 'Link has expired. Badge has already been Approved.'
      ? 'Link has expired. Badge has already been Approved.'
      : 'Invalid Token';

    return <InvalidTokenScreen errorMessage={errorHeader} />;
  }

  return (
    <Box
      style={{
        height: '100vh',
        background: 'linear-gradient(-20deg, #616161 0%, #9bc5c3 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Arial, sans-serif',
        position: 'fixed',
        width: '100%',
        top: 0,
        left: 0,
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>User SignUp</title>
      </Helmet>
      {showForm ? (
        <>
          <Container component={Paper} elevation={6} maxWidth="xs" style={{
            padding: 30,
            borderRadius: '15px',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
            position: 'relative',
            zIndex: 1,
          }}>
            <form onSubmit={formik.handleSubmit}>
              <Grid container justifyContent="center" alignItems="center">
                <Grid item>
                  <Avatar
                    src='/cirapng.png'
                    alt="Logo"
                    style={{
                      width: 80,
                      height: 100,
                      marginBottom: 15,
                      overflow: 'visible',
                    }}
                  />
                </Grid>
              </Grid>
              <Typography variant="h5" component="h2" gutterBottom style={{ textAlign: 'center', fontWeight: 'bold', color: '#333', marginBottom: 5 }}>
                User Signup
              </Typography>
              <Typography variant="subtitle1" component="h2" gutterBottom style={{ ...styles.textCenter, ...styles.smallText, marginBottom: 15 }}>
                Join Credential Intelligence Recognition
              </Typography>
              {errorMessage && (
                <Typography
                  variant="subtitle1"
                  component="h2"
                  gutterBottom
                  style={{
                    ...styles.textCenter,
                    marginBottom: 10,
                    color: 'red',
                    animation: 'blink 2s infinite',
                  }}
                >
                  {errorMessage}
                </Typography>
              )}
              {successMessage && (
                <Typography
                  variant="subtitle1"
                  component="h2"
                  gutterBottom
                  style={{
                    ...styles.textCenter,
                    marginBottom: 10,
                    color: 'green',
                    animation: 'blink 2s infinite',
                  }}
                >
                  {successMessage}
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      <>
                        {'First Name'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="firstName"
                    variant="outlined"
                    value={formik.values.firstName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                    helperText={formik.touched.firstName && formik.errors.firstName}
                    style={{ marginBottom: 5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      <>
                        {'Last Name'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="lastName"
                    variant="outlined"
                    value={formik.values.lastName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                    helperText={formik.touched.lastName && formik.errors.lastName}
                    style={{ marginBottom: 5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      <>
                        {'Email'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="email"
                    type="email"
                    variant="outlined"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    InputProps={{
                      readOnly: true,
                    }}
                    style={{ marginBottom: 5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label={
                      <>
                        {'Password'} <RedAsterisk>*</RedAsterisk>
                      </>
                    }
                    name="password"
                    type="password"
                    variant="outlined"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    style={{ marginBottom: 5 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    type="submit"
                    color="primary"
                    disabled={loading}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Sign Up'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Container>
        </>) : (
        <>
          <Box className={classes.modalContent} >
            <HowToRegIcon className={classes.icon} />
            <Typography id="modal-title" variant="h6" component="h2" align="center">
              Sign Up Required
            </Typography>
            <Typography id="modal-description" align="center" sx={{ mt: 2 }}>
              You need to sign up to approve this badge. Please create an account to continue.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              className={classes.button}
              onClick={() => setShowForm(true)}
            >
              SignUp
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default UserSignUp;
